import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Eye, EyeSlash, CheckCircle } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="w-10 h-10 rounded-full bg-[#8A9A86] flex items-center justify-center mb-6">
          <Leaf size={22} weight="fill" color="#fff" />
        </div>

        {done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle size={56} weight="duotone" color="#8A9A86" className="mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-semibold text-[#2C362B]">All set</h1>
            <p className="text-[#6D7A6A] mt-2">Your password has been updated. Taking you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-4xl font-semibold text-[#2C362B] mb-2">Choose a new password</h1>
            <p className="text-[#6D7A6A] mb-8 leading-relaxed">Make it something you'll remember and keep safe.</p>
            {!token ? (
              <p className="text-sm text-[#D97B6A] bg-[#FBEAE7] rounded-xl px-4 py-3">
                This link is missing its reset token. <Link to="/forgot-password" className="font-bold underline">Request a new one</Link>.
              </p>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="relative">
                  <input
                    type={show ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="w-full bg-[#F4F1E8] border-none rounded-2xl px-5 py-4 pr-12 text-[#2C362B] placeholder:text-[#A8B0A3] focus:ring-2 focus:ring-[#8A9A86] focus:bg-white outline-none transition-all"
                    data-testid="reset-password-input"
                  />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8A9A86]">
                    {show ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {error && <p className="text-sm text-[#D97B6A] bg-[#FBEAE7] rounded-xl px-4 py-3" data-testid="reset-error">{error}</p>}
                <button type="submit" disabled={loading} className="w-full bg-[#8A9A86] text-white hover:bg-[#6D7A6A] rounded-full px-8 py-4 font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-60" data-testid="reset-submit-btn">
                  {loading ? "Please wait…" : "Update password"}
                </button>
              </form>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}
